import { StatusCode } from '@/constants/statuses';
import { Proposal, SiteSurveyAsServiceInfo } from '@/controllers/Activity';
import { Contact } from '@/controllers/ShippingAddress';
import {
  AccessProgramType,
  ActivityType,
  DictionaryDTO,
  ProgramType,
  PropertyType,
  WatcherVm,
} from '@/types';

export enum StageCode {
  // Local stage - IS NOT PRESENT IN SALESFORCE - USE WITH CAUTION
  RequestNewProposal = 'RequestNewProposal',
  // Portal status - "Generating Proposal" always
  ProductQualified = 'Product Qualified',
  // Portal status - "Proposal Ready" if new proposal was not requested
  // Portal status - "Generating Proposal" if new proposal was requested
  ProposalGenerated = 'Proposal Generated',
  // Portal status - "Proposal Ready" if new proposal was not requested
  // Portal status - "Generating Proposal" if new proposal was requested
  AppointmentConfirmed = 'Appointment Confirmed',
  // Portal status - "Proposal Ready" if new proposal was not requested
  // Portal status - "Generating Proposal" if new proposal was requested
  FollowUp = 'Follow Up',
  // Portal status - "Ready to Order" if new proposal was not requested
  // Portal status - "Generating Proposal" if new proposal was requested
  ProposalAccepted = 'Proposal Accepted',
  // Portal status is based on the related Order status
  ClosedWon = 'Closed Won',
  // Portal status - "Lost" always
  ClosedLost = 'Closed Lost',
}

type ChangeStateInfoBase = {
  opportunityId: string;
  stageCode: DictionaryDTO['code'];
};

export type AcceptProposalInfo = {
  syncedProposalId: string;
  contactId: Contact['contactId'];
  firstName: Contact['firstName'];
  lastName: Contact['lastName'];
  email: Contact['email'];
  phoneNumber: Contact['phoneNumber'];
};

export type RequestNewProposalInfo = {
  newProposalDetails: string;
  newProposalReason: number;
  isRequestingNewProposal: true;
};

export type CloseLostInfo = {
  lossReason: string;
  lossDetails: string | null;
  syncedProposalId: string;
};

export type ChangeStageInfo = ChangeStateInfoBase &
  (AcceptProposalInfo | CloseLostInfo | RequestNewProposalInfo);

export type ChangeStagePayload = Partial<{
  stage: DictionaryDTO['id'];
  isRequestingNewProposal: boolean;
  proposalRequestDetails: string;
  syncedProposalId: string;
  lossReason: CloseLostInfo['lossReason'];
  lossDetails: CloseLostInfo['lossDetails'];
  newProposalDetails: string;
  newProposalReason: number;
}>;

export type Opportunity = {
  id: string;
  leadId: string;
  accessProgram: AccessProgramType | null;
  companyId: string;
  priceBookId: string | null;
  programType: ProgramType | null;
  propertyId: string | null;
  propertyType: PropertyType | null;
  contact: Contact;
  syncedProposalId: string | null;
  proposals: Proposal[];
  stage: DictionaryDTO['id'];
  name: string;
  isClosed: boolean;
  closeDate: string;
  activityType: ActivityType;
  activityStatus: number;
  statusColor: string;
  newProposalRequestDetails: null; // Requeriments to check what version of proposal is
  lossReason: string | null;
  specific: null; // specific/detailed loss reason
  bucketId: string | null;
  photoAlbumLink: string;
  asphaltShingleColor: string | null;
  salesforceBucketId: string | null;
  showSolarOnlyAccess: boolean | null;
  // Sales representative section
  salesRepresentativeId: string;
  salesRepresentativeEmail: string;
  salesRepresentativeFirstName: string;
  salesRepresentativeLastName: string;
  salesRepresentativePhone: string;
  // Proposal card
  proposalCardStatus: StatusCode | null;
  proposalCardStatusColor: string | null;
  messageBoardId: string | null;
  siteSurveyAsServiceInfo: SiteSurveyAsServiceInfo | null;
  watcherList: WatcherVm[] | null;
};

export enum AdditionalHomeownerOptionID {
  NewHomeowner = '-2',
  BulkPreOrder = '-1',
}

export type OpportunityShortInfo = {
  id: AdditionalHomeownerOptionID | Opportunity['id'];
  title: string;
  contact: Opportunity['contact'] | null;
  syncedProposalId: string | null;
};
